/**
 * Clinic portal massager API service.
 * Pure functions for massager init, search, detail, export, update. No React or localStorage.
 */

import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type {
  MassagerInitResponse,
  MassagerSearchResponse,
  MassagerDetailResponse,
  MassagerSearchFilters,
  BulkUpdateAlertRequest,
  BulkUpdateResponse,
  MassagerUpdateRequest,
} from '@/pages/main/massager/types';

export type { MassagerSearchFilters, BulkUpdateAlertRequest, MassagerUpdateRequest };

function toParams(filters: MassagerSearchFilters | undefined): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (!filters) return params;
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });
  return params;
}

/**
 * Get massager initialization data (prefectures, health center regist types, checkin_enabled).
 * @param siteType - Tenant site type (e.g. medix)
 * @param clinicNumber - Clinic number
 * @param serviceType - X-Service-Type header (A2 or R2)
 */
export async function getMassagerInit(
  siteType: string,
  clinicNumber: string,
  serviceType: string
): Promise<MassagerInitResponse> {
  const response = await apiClient.get<MassagerInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/massagers/init'),
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
}

/**
 * Search massagers with filters.
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param filters - Search filters
 * @param serviceType - X-Service-Type header
 */
export async function searchMassagers(
  siteType: string,
  clinicNumber: string,
  filters: MassagerSearchFilters,
  serviceType: string
): Promise<MassagerSearchResponse> {
  const response = await apiClient.get<MassagerSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/massagers/search'),
    { params: toParams(filters), headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
}

/**
 * Get massager detail by ID.
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param id - Massager ID
 * @param serviceType - X-Service-Type header
 */
export async function getMassagerDetail(
  siteType: string,
  clinicNumber: string,
  id: number,
  serviceType: string
): Promise<MassagerDetailResponse> {
  const response = await apiClient.get<MassagerDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/massagers/${id}`),
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
}

/**
 * Get all massagers (paginated).
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param perPage - Page size
 * @param page - Page number
 * @param serviceType - X-Service-Type header
 */
export async function getAllMassagers(
  siteType: string,
  clinicNumber: string,
  perPage: number,
  page: number,
  serviceType: string
): Promise<MassagerSearchResponse> {
  const response = await apiClient.get<MassagerSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/massagers'),
    {
      params: { per_page: perPage, page },
      headers: { 'X-Service-Type': serviceType },
    }
  );
  return response.data;
}

/**
 * Export massagers to CSV and trigger download.
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param filters - Optional filters for export
 * @param serviceType - A2 or R2 (used for header and filename)
 */
export async function exportMassagersCsv(
  siteType: string,
  clinicNumber: string,
  filters: MassagerSearchFilters | undefined,
  serviceType: 'A2' | 'R2'
): Promise<void> {
  const response = await apiClient.get(clinicApiPath(siteType, clinicNumber, '/massagers/export-csv'), {
    params: toParams(filters),
    responseType: 'blob',
    headers: { 'X-Service-Type': serviceType },
  });

  const contentDisposition = response.headers['content-disposition'];
  const defaultFilename =
    serviceType === SERVICE_TYPE_A2 ? 'はり・きゅう師一覧.csv' : 'マッサージ師一覧.csv';
  let filename = defaultFilename;
  if (contentDisposition) {
    const utf8FilenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/i);
    if (utf8FilenameMatch && utf8FilenameMatch[1]) {
      try {
        filename = decodeURIComponent(utf8FilenameMatch[1]);
      } catch (e) {
        filename = utf8FilenameMatch[1];
      }
    } else if (match && match[1]) {
      try {
        filename = decodeURIComponent(match[1]);
      } catch (e) {
        filename = match[1];
      }
    }
  }

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Update single massager (detail modal: email, check-in settings).
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param id - Massager ID
 * @param data - Update payload
 * @param serviceType - X-Service-Type header
 */
export async function updateMassagerDetail(
  siteType: string,
  clinicNumber: string,
  id: number,
  data: MassagerUpdateRequest,
  serviceType: string
): Promise<MassagerDetailResponse> {
  const response = await apiClient.put<MassagerDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/massagers/${id}`),
    data,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
}

/**
 * Bulk update alert settings for selected massagers (一括設定).
 * @param siteType - Tenant site type
 * @param clinicNumber - Clinic number
 * @param data - Bulk update payload
 * @param serviceType - X-Service-Type header
 */
export async function bulkUpdateMassagerAlert(
  siteType: string,
  clinicNumber: string,
  data: BulkUpdateAlertRequest,
  serviceType: string
): Promise<BulkUpdateResponse> {
  const response = await apiClient.post<BulkUpdateResponse>(
    clinicApiPath(siteType, clinicNumber, '/massagers/bulk-update-alert'),
    data,
    { headers: { 'X-Service-Type': serviceType } }
  );
  return response.data;
}
